import { A_LA_CARTE_PRICE, COMPANY_TIER } from '@demandproai/shared';
import useLifecarePlanDocument from '../../hooks/useLifecarePlanDocument';
import useCompanyTier from '../../hooks/useCompanyTier';
import useCostProjectionDownload from '../../hooks/useCostProjectionDownload';
import PreviewSectionCard from './PreviewSectionCard';
import PreviewBadge from './PreviewBadge';
import TextSection from './TextSection';
import FutureCareTable from './FutureCareTable';
import LifecarePlanTotalValue from './LifecarePlanTotalValue';
import CostProjectionPurchaseModal from './CostProjectionPurchaseModal';
import XButton from '../common/XButton';

const docClasses = 'rounded-xl border border-border-light bg-surface-card p-7 font-secondary';

const docTitleClasses = 'text-3xl font-bold text-content text-center m-0';

const docMetaClasses = 'text-center text-base text-subtle mt-1 mb-6';

const sectionHeadingClasses =
    'text-base font-bold uppercase tracking-wide text-content border-b border-border pb-1.5 mb-2 m-0';

const sectionBodyClasses = 'text-base leading-relaxed text-content whitespace-pre-line m-0';

const gatedWrapClasses = 'relative';

const gatedContentClasses = 'blur-sm select-none pointer-events-none';

const gatedOverlayClasses =
    'absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-6';

const lockBadgeClasses =
    'flex items-center justify-center h-12 w-12 rounded-full bg-primary text-white! text-lg shadow-lg';

const lockTitleClasses = 'text-base font-bold text-content m-0';

const priceLabel = `$${A_LA_CARTE_PRICE.COST_PROJECTION[COMPANY_TIER.SILVER].toFixed(2)}`;

const LifecarePlanDocumentCard = ({ plan, plaintiffName, caseId, clientId }) => {
    const {
        info,
        planMeta,
        diagnosisTitle,
        diagnosisBody,
        lifeExpectancyTitle,
        lifeExpectancyBody,
        lifeExpectancyYears,
        summaryTitle,
        summaryBody,
        futureCareTitle,
        subsections,
        hasPlanValue,
        totalValueFormatted,
        totalValueLowFormatted,
        totalValueHighFormatted,
    } = useLifecarePlanDocument({ plan });
    const { isSilverTier } = useCompanyTier();
    const {
        canDownload,
        purchaseModalOpen,
        isDownloading,
        handleDownloadClick,
        confirmDownload,
        closeModal,
    } = useCostProjectionDownload({ caseId, clientId });

    const futureCareTables = subsections.map((subsection, index) => (
        <FutureCareTable key={subsection.id || `${subsection.title}-${index}`} subsection={subsection} />
    ));

    const downloadAction = canDownload ? (
        <XButton variant="outlinePrimary" size="sm" onClick={handleDownloadClick} loading={isDownloading}>
            <i className="fas fa-download mr-2" />
            Download
        </XButton>
    ) : null;

    return (
        <PreviewSectionCard
            title="Future Cost Projection Preview"
            badge={isSilverTier ? <PreviewBadge tone="muted">Future care needs locked</PreviewBadge> : null}
            action={downloadAction}
        >
            <div className={docClasses}>
                <h3 className={docTitleClasses}>{plaintiffName || info?.clientName || 'Plaintiff'}</h3>
                <p className={docMetaClasses}>{planMeta}</p>

                <TextSection heading={diagnosisTitle} body={diagnosisBody} />
                <TextSection
                    heading={lifeExpectancyTitle}
                    body={lifeExpectancyBody}
                    trailing={
                        lifeExpectancyYears > 0 ? (
                            <p className={`${sectionBodyClasses} mt-1 font-semibold`}>
                                {lifeExpectancyYears} years remaining
                            </p>
                        ) : null
                    }
                />
                <TextSection heading={summaryTitle} body={summaryBody} />

                <h4 className={sectionHeadingClasses}>{futureCareTitle}</h4>
                {hasPlanValue && (
                    <LifecarePlanTotalValue
                        totalValueFormatted={totalValueFormatted}
                        totalValueLowFormatted={totalValueLowFormatted}
                        totalValueHighFormatted={totalValueHighFormatted}
                    />
                )}
                {isSilverTier ? (
                    <div className={gatedWrapClasses}>
                        <div className={gatedContentClasses} aria-hidden="true">
                            {futureCareTables}
                        </div>
                        <div className={gatedOverlayClasses}>
                            <span className={lockBadgeClasses}>
                                <i className="fas fa-lock" />
                            </span>
                            <p className={lockTitleClasses}>Future Care Needs locked</p>                          
                        </div>
                    </div>
                ) : (
                    futureCareTables
                )}
            </div>
            <CostProjectionPurchaseModal
                isOpen={purchaseModalOpen}
                isLoading={isDownloading}
                priceLabel={priceLabel}
                onConfirm={confirmDownload}
                onCancel={closeModal}
            />
        </PreviewSectionCard>
    );
};

export default LifecarePlanDocumentCard;
